import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService, type IAPIResponse } from '@/services/api'
import { useAuthStore } from './useAuthStore'

export interface CompanyDetails {
  companyAddress: string
  companyPhone: string
  companyEmail: string
  companyTIN: string | null
  companyName: string
}

// Types for sales transactions
export interface SaleItem {
  sageItemCode: string
  sageStockLink: number
  eisProductCode: string
  productName: string
  barcode: string
  quantity: number
  unitPrice: number
  totalPrice: number
  lineTotal: number | null
  discountPercent?: number
  effectivePrice?: number
  sellingPrice?: number
  itemDiscount?: number
  taxRate: number
  taxRateId: string
  isProduct?: boolean
}
export interface TransactionItemRequest {
  inventoryId: number
  productId: number
  productName: string
  productCode: string
  barcode: string
  quantity: number
  unitPrice: number
  totalPrice: number
  discountPercent?: number
}

interface TaxBreakDown {
  rateId: string
  taxableAmount: number
  taxAmount: number
  rateValue: number
}

interface TaxBreakdown {
  subtotal: number
  totalItemDiscount: number
  receiptDiscountAmount: number
  taxBreakdowns: TaxBreakDown[]
  totalTax: number
}

export interface SalesResponse {
  transactionId: string
  receiptNumber: string
  validationURL?: string
  isOnline?: boolean
  isBlocked?: boolean | null
  shouldRefreshConfigurations?: boolean | null
  companyDetails?: CompanyDetails | null
}

export interface SaleTransaction {
  transactionId?: string
  locationId: number
  locationName?: string
  cashierId?: number
  cashierName: string
  customerId?: number
  customerName?: string
  customerPhone?: string
  items: SaleItem[]
  subtotal: number
  discountPercent: number
  discountAmount: number
  taxRate: number
  taxAmount: number
  total: number
  paymentMethod: string
  cashReceived?: number
  change?: number
  status: 'pending' | 'completed' | 'cancelled' | 'refunded'
  transactionDate: string
  companyCode: string
  receiptNumber: string | number
  buyerTIN?: string
  authorizationCode?: string
  totalVAT?: number
  isExport?: boolean
  isReliefSupply?: boolean
  vatCertificate?: any
  validationURL?: string
}

export interface SalesSummaryDto {
  totalSales: number
  totalTransactions: number
  totalTax: number
  totalDiscounts: number
  averageTransaction: number
  onlineSales: number
  offlineSales: number
  onlineSalesAmount: number
  offlineSalesAmount: number
  startDate: string
  endDate: string
}

export interface OnlineSalesDto {
  transactionId: number
  transactionNumber: string
  transactionDate: string
  totalAmount: number
  taxAmount: number
  paymentMethod: string
  validationUrl: string
  cashierName: string
}

export interface OfflineSalesDto {
  transactionId: number
  transactionNumber: string
  transactionDate: string
  totalAmount: number
  taxAmount: number
  paymentMethod: string
  isPushed: boolean
  offlineSignature: string
  cashierName: string
}

export interface TopProductReportDto {
  productCode: string
  productName: string
  totalQuantitySold: number
  totalRevenue: number
  transactionCount: number
  averagePrice: number
}

export interface TransactionFromAPI {
  transactionId: number
  transactionNumber: string
  customerName: string | null
  eisValidationUrl: string | null
  transactionDate: string
  subTotal: number
  taxAmount: number
  discountAmount: number
  totalAmount: number
  paymentMethod: 'cash' | 'card' | 'mobile' | string
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED' | string
  cashierName: string
  items: SaleItem[]
}

export interface TransactionDetail {
  transactionDetailId: number
  transactionId: number
  inventoryId: number
  variantId: number
  quantity: number
  unitPrice: number
  discountPercent: number
  discountAmount: number
  taxPercent: number
  taxAmount: number
  lineTotal: number
  createdDate: string
}

export interface MonthlySalesData {
  revenue: number
  transactionCount: number
  itemsSold: number
  averageTransactionValue: number
}

export interface GrowthMetrics {
  revenueGrowthPercentage: number
  transactionGrowthPercentage: number
  revenueChange: number
  transactionChange: number
}

export interface LocationSalesComparison {
  locationId: number
  locationName: string
  currentMonth: MonthlySalesData
  previousMonth: MonthlySalesData
  growth: GrowthMetrics
}

export interface DailySalesReport {
  date: string
  totalSales: number
  totalTransactions: number
  totalItems: number
  averageTransaction: number
  paymentMethods: Record<string, number>
}

export const useSalesStore = defineStore('sales', () => {
  // State
  const transactions = ref<TransactionFromAPI[]>([])
  const currentTransaction = ref<Partial<SaleTransaction> | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const dailyReport = ref<DailySalesReport | null>(null)
  const locationComparisons = ref<LocationSalesComparison[]>([])
  const locationComparisonsLoading = ref(false)
  const companyDetails = ref<CompanyDetails | null>(null)

  const salesSummary = ref<SalesSummaryDto | null>(null)
  const onlineSales = ref<OnlineSalesDto[]>([])
  const offlineSales = ref<OfflineSalesDto[]>([])
  const topProducts = ref<TopProductReportDto[]>([])

  // Get auth store for company and user info
  const authStore = useAuthStore()

  // Getters
  const todaysTransactions = computed(() => {
    const today = new Date().toISOString().split('T')[0]
    return transactions.value.filter((t) => t.transactionDate.split('T')[0] === today)
  })

  const todaysTotalSales = computed(() => {
    return todaysTransactions.value.reduce((sum, t) => sum + t.totalAmount, 0)
  })

  const completedTransactions = computed(() =>
    transactions.value.filter((t) => t.status.toLowerCase() === 'completed'),
  )

  const topPerformingLocations = computed(() => {
    return locationComparisons.value
      .filter((loc) => loc.growth.revenueGrowthPercentage > 0)
      .sort((a, b) => b.growth.revenueGrowthPercentage - a.growth.revenueGrowthPercentage)
      .slice(0, 5)
  })

  const underperformingLocations = computed(() => {
    return locationComparisons.value
      .filter((loc) => loc.growth.revenueGrowthPercentage < 0)
      .sort((a, b) => a.growth.revenueGrowthPercentage - b.growth.revenueGrowthPercentage)
      .slice(0, 5)
  })

  const totalCompanyGrowth = computed(() => {
    if (locationComparisons.value.length === 0) return 0

    const totalCurrentRevenue = locationComparisons.value.reduce(
      (sum, loc) => sum + loc.currentMonth.revenue,
      0,
    )
    const totalPreviousRevenue = locationComparisons.value.reduce(
      (sum, loc) => sum + loc.previousMonth.revenue,
      0,
    )

    if (totalPreviousRevenue === 0) {
      return totalCurrentRevenue === 0 ? 0 : 100
    }

    return ((totalCurrentRevenue - totalPreviousRevenue) / totalPreviousRevenue) * 100
  })

  const getLocationPerformanceData = computed(() => {
    return locationComparisons.value.map((location) => ({
      id: location.locationId.toString(),
      name: location.locationName,
      sales: location.currentMonth.transactionCount,
      revenue: location.currentMonth.revenue,
      growth: location.growth.revenueGrowthPercentage,
    }))
  })

  // Actions
  const generateTransactionId = (): string => {
    const timestamp = Date.now().toString()
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0')
    return `TXN${timestamp}${random}`
  }

  const loadLocationSalesComparison = async (): Promise<void> => {
    locationComparisonsLoading.value = true
    error.value = null

    try {
      const response = await apiService.get<IAPIResponse<LocationSalesComparison[]>>(
        `/sales/sales-comparison-by-location`,
      )

      if (response.isSuccessful && response.payload) {
        locationComparisons.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load location sales comparison'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load location sales comparison'
    } finally {
      locationComparisonsLoading.value = false
    }
  }

  // ─── Replace these four methods in your sales store ──────────────────────────
  // Only the method signatures and params change; everything else stays the same.

  const getSalesSummary = async (
    startDate?: string,
    endDate?: string,
    isCashier: boolean = false, // ← NEW
  ) => {
    loading.value = true
    error.value = null
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      if (isCashier) params.append('isCashier', 'true') // ← NEW

      const response = await apiService.get<IAPIResponse<SalesSummaryDto>>(
        `/sales/reports/sales-summary?${params.toString()}`,
      )
      if (response.isSuccessful && response.payload) {
        salesSummary.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load sales summary'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load sales summary'
    } finally {
      loading.value = false
    }
  }

  const getOnlineSales = async (
    startDate?: string,
    endDate?: string,
    page = 1,
    pageSize = 50,
    isCashier: boolean = false, // ← NEW
  ) => {
    loading.value = true
    error.value = null
    try {
      const params = new URLSearchParams({ page: page.toString(), pageSize: pageSize.toString() })
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      if (isCashier) params.append('isCashier', 'true') // ← NEW

      const response = await apiService.get<IAPIResponse<{ transactions: OnlineSalesDto[] }>>(
        `/sales/reports/online-sales?${params.toString()}`,
      )
      if (response.isSuccessful && response.payload) {
        onlineSales.value = response.payload.transactions
      } else {
        error.value = response.remark || 'Failed to load online sales'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load online sales'
    } finally {
      loading.value = false
    }
  }

  const getOfflineSales = async (
    startDate?: string,
    endDate?: string,
    isPushed?: boolean,
    page = 1,
    pageSize = 50,
    isCashier: boolean = false, // ← NEW
  ) => {
    loading.value = true
    error.value = null
    try {
      const params = new URLSearchParams({ page: page.toString(), pageSize: pageSize.toString() })
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      if (isPushed !== undefined) params.append('isPushed', isPushed.toString())
      if (isCashier) params.append('isCashier', 'true') // ← NEW

      const response = await apiService.get<IAPIResponse<{ transactions: OfflineSalesDto[] }>>(
        `/sales/reports/offline-sales?${params.toString()}`,
      )
      if (response.isSuccessful && response.payload) {
        offlineSales.value = response.payload.transactions
      } else {
        error.value = response.remark || 'Failed to load offline sales'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load offline sales'
    } finally {
      loading.value = false
    }
  }

  const getTopProducts = async (
    startDate?: string,
    endDate?: string,
    top = 10,
    isCashier: boolean = false, // ← NEW
  ) => {
    loading.value = true
    error.value = null
    try {
      const params = new URLSearchParams({ top: top.toString() })
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      if (isCashier) params.append('isCashier', 'true') // ← NEW

      const response = await apiService.get<IAPIResponse<TopProductReportDto[]>>(
        `/sales/reports/top-products?${params.toString()}`,
      )
      if (response.isSuccessful && response.payload) {
        topProducts.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load top products'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load top products'
    } finally {
      loading.value = false
    }
  }

  const processTransaction = async (
    transactionData: any,
  ): Promise<{
    success: boolean
    data?: SalesResponse
    error?: string
    isBlocked?: boolean
    shouldRefreshConfigurations?: boolean
  }> => {
    loading.value = true
    error.value = null

    try {
      const isEISTransaction = 'invoiceHeader' in transactionData

      if (isEISTransaction) {
        currentTransaction.value = {
          transactionId: transactionData.invoiceHeader.invoiceNumber || generateTransactionId(),
          locationId: parseInt(transactionData.invoiceHeader.siteId),
          locationName: transactionData.locationName,
          cashierId: authStore.user?.userId,
          cashierName: authStore.user?.username || '',
          customerName: transactionData.invoiceHeader.buyerName,
          customerPhone: transactionData.customer?.phone || '',
          buyerTIN: transactionData.invoiceHeader.buyerTIN || '',
          authorizationCode: transactionData.invoiceHeader.buyerAuthorizationCode || '',
          items: transactionData.invoiceLineItems.map((lineItem: any) => ({
            productCode: lineItem.productCode,
            productName: lineItem.description,
            quantity: lineItem.quantity,
            unitPrice: lineItem.unitPrice,
            totalPrice: lineItem.total,
            discountPercent: lineItem.discount,
            eisTaxRate: lineItem.taxRateId,
            isProduct: lineItem.isProduct,
            inventoryId: lineItem.inventoryId,
            productId: lineItem.productId,
            eisTaxRateValue: lineItem.eisTaxRateValue,
          })),
          subtotal:
            transactionData.invoiceSummary.invoiceTotal - transactionData.invoiceSummary.totalVAT,
          taxAmount: transactionData.invoiceSummary.totalVAT,
          totalVAT: transactionData.invoiceSummary.totalVAT,
          total: transactionData.invoiceSummary.invoiceTotal,
          paymentMethod: transactionData.paymentMethod,
          cashReceived:
            transactionData.cashReceived || transactionData.invoiceSummary.amountTendered,
          change: transactionData.change || 0,
          status: 'pending',
          transactionDate: transactionData.invoiceHeader.invoiceDateTime,
          isExport: transactionData.invoiceHeader.isExport,
          isReliefSupply: transactionData.invoiceHeader.isReliefSupply,
          vatCertificate: transactionData.invoiceHeader.vat5CertificateDetails,
        }
      } else {
        // For non-EIS transactions, use as-is
        currentTransaction.value = transactionData
      }

      const response = await apiService.post<IAPIResponse<SalesResponse>>(
        '/Sales/transactions',
        transactionData,
      )

      if (response.isSuccessful && response.payload) {
        if (currentTransaction.value) {
          currentTransaction.value.transactionId = response.payload.transactionId
          currentTransaction.value.receiptNumber = response.payload.receiptNumber
          currentTransaction.value.status = 'completed'
          currentTransaction.value.validationURL = response.payload.validationURL
        }

        companyDetails.value = response.payload.companyDetails || null
        return {
          success: true,
          data: response.payload,
        }
      } else {
        error.value = response.remark || 'Failed to process transaction'
        return {
          isBlocked: response.payload?.isBlocked || undefined,
          success: false,
          error: error.value,
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process transaction'
      error.value = errorMessage
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      loading.value = false
    }
  }
  const refundTransaction = async (transactionId: string, reason?: string): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.post<IAPIResponse<boolean>>(
        `/Sales/transactions/${transactionId}/refund`,
        { reason },
      )

      if (response.isSuccessful) {
        const transaction = transactions.value.find((t) => t.transactionNumber === transactionId)
        if (transaction) {
          transaction.status = 'REFUNDED'
        }
        return true
      } else {
        error.value = response.remark || 'Failed to refund transaction'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to refund transaction'
      return false
    } finally {
      loading.value = false
    }
  }

  const loadTransactions = async (options?: {
    startDate?: string
    endDate?: string
    locationId?: number
    status?: string
    page?: number
    pageSize?: number
    isCashier: boolean
  }): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams({
        page: (options?.page || 1).toString(),
        pageSize: (options?.pageSize || 50).toString(),
      })

      if (options?.startDate) params.append('startDate', options.startDate)
      if (options?.endDate) params.append('endDate', options.endDate)
      if (options?.locationId) params.append('locationId', options.locationId.toString())
      if (options?.status) params.append('status', options.status)
      if (options?.isCashier) params.append('isCashier', options.isCashier.toString())

      const response = await apiService.get<IAPIResponse<TransactionFromAPI[]>>(
        `/sales/transactions?${params.toString()}`,
      )

      if (response.isSuccessful && response.payload) {
        transactions.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load transactions'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load transactions'
    } finally {
      loading.value = false
    }
  }

  const generateDailyReport = async (date?: string, locationId?: number): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      if (date) params.append('date', date)
      if (locationId) params.append('locationId', locationId.toString())

      const response = await apiService.get<IAPIResponse<DailySalesReport>>(
        `/sales/reports/daily?${params.toString()}`,
      )

      if (response.isSuccessful && response.payload) {
        dailyReport.value = response.payload
      } else {
        error.value = response.remark || 'Failed to generate daily report'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to generate daily report'
    } finally {
      loading.value = false
    }
  }

  const getTransactionById = (transactionId: string): TransactionFromAPI | undefined => {
    return transactions.value.find((t) => t.transactionNumber === transactionId)
  }

  const clearError = () => {
    error.value = null
  }

  const clearCurrentTransaction = () => {
    currentTransaction.value = null
  }

  const getTotalRevenue = (): number => {
    return completedTransactions.value.reduce(
      (sum, transaction) => sum + transaction.totalAmount,
      0,
    )
  }

  const getRecentSales = (limit: number = 10): TransactionFromAPI[] => {
    return [...transactions.value]
      .filter((t) => t.status.toLowerCase() === 'completed')
      .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())
      .slice(0, limit)
  }

  const getSalesByLocation = async (locationId: number): Promise<SaleTransaction[]> => {
    try {
      const response = await apiService.get<IAPIResponse<SaleTransaction[]>>(
        `/Sales/sales-by-location/${locationId}`,
      )

      if (response.isSuccessful && response.payload) {
        return response.payload
      } else {
        console.error('Failed to fetch sales by location:', response.remark)
        return []
      }
    } catch (err) {
      console.error('Error fetching sales by location:', err)
      return []
    }
  }

  const getRevenueByLocation = async (locationId: number): Promise<number> => {
    try {
      const sales = await getSalesByLocation(locationId)
      return sales.reduce((sum, sale) => sum + (sale.total || 0), 0)
    } catch (err) {
      console.error('Error fetching revenue by location:', err)
      return 0
    }
  }

  const getPreviousRevenueByLocation = (locationId: number): number => {
    const previousPeriodStart = new Date()
    previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 2)
    const previousPeriodEnd = new Date()
    previousPeriodEnd.setMonth(previousPeriodEnd.getMonth() - 1)

    return transactions.value
      .filter(
        (t) =>
          t.status.toLowerCase() === 'completed' &&
          new Date(t.transactionDate) >= previousPeriodStart &&
          new Date(t.transactionDate) < previousPeriodEnd,
      )
      .reduce((sum, sale) => sum + sale.totalAmount, 0)
  }

  const calculateTrendPercentage = (
    current: SaleTransaction[],
    previous: SaleTransaction[],
  ): number => {
    const currentTotal = current.reduce((sum, sale) => sum + sale.total, 0)
    const previousTotal = previous.reduce((sum, sale) => sum + sale.total, 0)

    if (previousTotal === 0) return currentTotal > 0 ? 100 : 0

    return ((currentTotal - previousTotal) / previousTotal) * 100
  }

  const getDailyAverageSales = (): number => {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

    const recentSales = completedTransactions.value.filter(
      (sale) => new Date(sale.transactionDate) >= thirtyDaysAgo,
    )

    const totalRevenue = recentSales.reduce((sum, sale) => sum + sale.totalAmount, 0)
    return totalRevenue / 30
  }

  const getGrowthByLocation = async (
    locationId: number,
  ): Promise<{
    locationId: number
    currentRevenue: number
    previousRevenue: number
    growthPercentage: number
  }> => {
    try {
      const result = await getSalesForPeriod(locationId, 'current')
      return result
    } catch (err) {
      console.error('Error calculating growth by location:', err)
      return {
        locationId,
        currentRevenue: 0,
        previousRevenue: 0,
        growthPercentage: 0,
      }
    }
  }

  const getSalesForPeriod = async (
    locationId: number,
    period: 'current' | 'previous',
  ): Promise<{
    locationId: number
    currentRevenue: number
    previousRevenue: number
    growthPercentage: number
  }> => {
    const now = new Date()
    let startDate: string, endDate: string

    if (period === 'current') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      endDate = now.toISOString()
    } else {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString()
      endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString()
    }

    try {
      const response = await apiService.get<
        IAPIResponse<{
          locationId: number
          currentRevenue: number
          previousRevenue: number
          growthPercentage: number
        }>
      >(
        `/Sales/growth-by-location?locationId=${locationId}&startDate=${startDate}&endDate=${endDate}`,
      )

      if (response.isSuccessful && response.payload) {
        return response.payload
      } else {
        console.error('Failed to fetch sales for period:', response.remark)
        return {
          locationId,
          currentRevenue: 0,
          previousRevenue: 0,
          growthPercentage: 0,
        }
      }
    } catch (err) {
      console.error('Error fetching sales for period:', err)
      return {
        locationId,
        currentRevenue: 0,
        previousRevenue: 0,
        growthPercentage: 0,
      }
    }
  }

  const getCashFlowTrend = (): Array<{
    date: string
    inflow: number
    outflow: number
    netFlow: number
  }> => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - i)
      return date.toISOString().split('T')[0]
    }).reverse()

    return last30Days.map((date) => {
      const dailySales = completedTransactions.value
        .filter((t) => t.transactionDate.split('T')[0] === date)
        .reduce((sum, t) => sum + t.totalAmount, 0)

      const dailyRefunds = transactions.value
        .filter(
          (t) => t.transactionDate.split('T')[0] === date && t.status.toLowerCase() === 'refunded',
        )
        .reduce((sum, t) => sum + t.totalAmount, 0)

      return {
        date,
        inflow: dailySales,
        outflow: dailyRefunds,
        netFlow: dailySales - dailyRefunds,
      }
    })
  }

  return {
    // State
    transactions,
    currentTransaction,
    companyDetails,
    loading,
    error,
    dailyReport,
    locationComparisons,
    locationComparisonsLoading,
    salesSummary,
    onlineSales,
    offlineSales,
    topProducts,

    // Getters
    todaysTransactions,
    todaysTotalSales,
    completedTransactions,
    topPerformingLocations,
    underperformingLocations,
    totalCompanyGrowth,
    getLocationPerformanceData,

    // Actions
    processTransaction,
    refundTransaction,
    loadTransactions,
    generateDailyReport,
    getTransactionById,
    clearError,
    clearCurrentTransaction,
    generateTransactionId,
    getCashFlowTrend,
    getDailyAverageSales,
    getPreviousRevenueByLocation,
    getRevenueByLocation,
    getRecentSales,
    getTotalRevenue,
    calculateTrendPercentage,
    getSalesForPeriod,
    getGrowthByLocation,
    getSalesByLocation,
    loadLocationSalesComparison,

    getSalesSummary,
    getOnlineSales,
    getOfflineSales,
    getTopProducts,
  }
})
